import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ButtonModule,
  ConfirmationService,
  DialogModule,
  DropdownModule,
  InputSwitchModule,
  TableModule,
} from 'primeng';
import { SelectItem } from 'primeng/api';
import { VmeCommonModule } from '../../vme-common.module';
import { CustomerService, OrganisationsService } from '../../services';
import { OrderBookingHealthCheckService } from '../../services/order-booking-health-check.service';
import { IOrderBookingCheckConfiguration } from '../../interfaces/order-booking-health-check/order-booking-check-configuration';
import { OrderBookingCheckInfoComponent } from '../order-booking-health-check/order-booking-check-info/order-booking-check-info.component';
import { IDepartment } from '../../interfaces/department';
import { Message } from '../../classes/Message';

@Component({
  selector: 'order-booking-check-configuration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    VmeCommonModule,
    TableModule,
    DialogModule,
    DropdownModule,
    ButtonModule,
    InputSwitchModule,
    OrderBookingCheckInfoComponent,
  ],
  templateUrl: './order-booking-check-configuration.component.html',
})
export class OrderBookingCheckConfigurationComponent implements OnInit {
  protected configurations: IOrderBookingCheckConfiguration[] = [];
  protected checkTypes: SelectItem[] = [];
  protected customerOptions: SelectItem[] = [];

  // Fields rather than getters: PrimeNG realigns an open panel whenever the options array is a new
  // instance, and a getter makes it one on every change-detection pass.
  protected departmentOptions: SelectItem[] = [];
  protected teamOptions: SelectItem[] = [];

  protected isLoading = true;
  protected saving = false;
  protected showDialog = false;
  protected editing: IOrderBookingCheckConfiguration;
  protected togglingId: number;

  private departments: IDepartment[] = [];

  constructor(
    private orderBookingHealthCheckService: OrderBookingHealthCheckService,
    private organisationsService: OrganisationsService,
    private customerService: CustomerService,
    private confirmationService: ConfirmationService,
  ) {}

  async ngOnInit() {
    // Reference data for the add dialog. Guarded because a failure would otherwise abort before
    // load(), leaving the page on its loading overlay for good.
    try {
      const customers = await this.organisationsService.getAllCustomers();

      this.customerOptions = [
        { value: null, label: 'All customers' },
        ...customers.map((c) => ({ value: c.id, label: c.name })),
      ];

      // The check list is stable reference data, so it comes back with the filter options.
      const filterOptions = await this.orderBookingHealthCheckService.getFilterOptions();
      this.checkTypes = filterOptions.checkTypes;
    } catch {
      Message.Error('Could not load the customer and check lists. Please refresh to try again.');
    }

    await this.load();
  }

  protected async load() {
    this.isLoading = true;
    try {
      this.configurations = await this.orderBookingHealthCheckService.getConfiguration();
    } catch {
      this.configurations = [];
      Message.Error('Could not load the configured rules. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  protected async addNew() {
    this.editing = {
      id: 0,
      orderBookingCheckId: null,
      orderBookingCheckTitle: '',
      customerId: null,
      customerName: '',
      customerDepartmentId: null,
      customerDepartmentName: '',
      customerTeamId: null,
      customerTeamName: '',
      isEnabled: true,
    };
    this.showDialog = true;
    await this.refreshScopeOptions();
  }

  protected async edit(configuration: IOrderBookingCheckConfiguration) {
    this.editing = { ...configuration };
    this.showDialog = true;
    await this.refreshScopeOptions();
  }

  // Narrowing the scope upwards has to clear anything more specific, or the saved row would claim
  // a scope the user did not choose.
  protected async onCustomerChanged() {
    this.editing.customerDepartmentId = null;
    this.editing.customerTeamId = null;
    await this.refreshScopeOptions();
  }

  // No refetch: the teams arrived with the departments.
  protected onDepartmentChanged() {
    this.editing.customerTeamId = null;
    this.buildTeamOptions();
  }

  // Fetched per customer rather than all at once, because this is the endpoint that carries each
  // department's teams. No customer means the rule is global, so nothing below it can be picked.
  private async refreshScopeOptions() {
    const customerId = this.editing.customerId;
    let departments: IDepartment[] = [];

    if (customerId) {
      this.departmentOptions = [{ label: 'Loading...', value: 0, disabled: true }];

      try {
        departments = await this.customerService.getDepartments(customerId);
      } catch {
        Message.Error('Could not load the departments for that customer.');
      }

      // Nothing is committed until here, so a later change that already landed keeps its lists.
      if (customerId !== this.editing.customerId) return;
    }

    this.departments = departments;
    this.departmentOptions = [
      { value: null, label: 'All departments' },
      ...departments.map((d) => ({ value: d.id, label: d.name })),
    ];

    this.buildTeamOptions();
  }

  private buildTeamOptions() {
    const department = this.departments.find((d) => d.id === this.editing.customerDepartmentId);

    this.teamOptions = [
      { value: null, label: 'All teams' },
      ...(department?.teams ?? []).map((t) => ({ value: t.id, label: t.name })),
    ];
  }

  protected async save() {
    if (!this.editing.orderBookingCheckId) {
      Message.Error('Please choose which check this applies to.');
      return;
    }

    this.saving = true;
    try {
      await this.orderBookingHealthCheckService.upsertConfiguration(this.editing);
      this.showDialog = false;
      await this.load();
    } catch (error: any) {
      Message.Error(this.reason(error) ?? 'Could not save the rule. Please try again.');
    } finally {
      this.saving = false;
    }
  }

  // Writes straight through, since turning a check on or off is what the screen is for. A refusal
  // reloads rather than reverting: restoring the flag moves no binding, so the switch would stick.
  protected async toggleEnabled(
    configuration: IOrderBookingCheckConfiguration,
    isEnabled: boolean,
  ) {
    this.togglingId = configuration.id;
    try {
      await this.orderBookingHealthCheckService.upsertConfiguration({
        ...configuration,
        isEnabled,
      });
      configuration.isEnabled = isEnabled;
    } catch (error: any) {
      Message.Error(this.reason(error) ?? 'Could not change the rule. Please try again.');
      await this.load();
    } finally {
      this.togglingId = null;
    }
  }

  protected remove(configuration: IOrderBookingCheckConfiguration) {
    this.confirmationService.confirm({
      message: 'Remove this rule? The check will fall back to the next broadest rule.',
      accept: async () => {
        try {
          await this.orderBookingHealthCheckService.deleteConfiguration(configuration.id);
        } catch {
          Message.Error('Could not remove the rule. Please try again.');
        }

        await this.load();
      },
    });
  }

  // A refused rule says why - usually that one already covers this scope, or that the department
  // and customer do not belong together.
  private reason(error: any): string {
    return error?.error?.validationErrors?.[0]?.errorMessage;
  }
}
